import { Vector2 } from "three";
import { Scene as Canvas2dutilScene } from "../utils";
import { FeatureStyle, GEOMETRYTYPE } from "../featuretree/internal";
import { USERINPUTTYPE } from "../userinput/internal";
import { ExternalMeasureScene, ImageObservation, MeasureSequence, STREAMTYPE, } from "./internal";
/**
 * MultiViewMeasureSequence
 * @class MultiViewMeasureSequence
 * @author Benjamin Loesch
 */
export class PanoTwoClickMeasureSequence extends MeasureSequence {
    /**
     * Creates a new scene window and adds it as child element to the main scene
     * mx,my are mouse positons a user is looking for a second point from a different view
     * @method constructor
     * @param {Scene} scene
     * @param successcbf
     * @param failedcbf
     * @param mx
     * @param my
     */
    constructor(scene, successcbf, failedcbf, mx, my) {
        super(scene, successcbf, failedcbf);
        this.keydeleteeventnmbr = null;
        this.featurecollection = null;
        this.pointfeature = null;
        this.pos = null;
        this.scene2d = null;
        this.imageobservations = [];
        /**
         * approximative value read by monoplotting
         */
        this.approxval = null;
        this.engine = null;
        this.multiviewmeasurepoint = null;
        this.panoroteventnr = -1;
        this.panozoomeventnr = -1;
        /**
         * Indicates the position a user first clicked on
         * @type {Crosshair} crosshair
         */
        this.crosshair = null;
        this.engine = this.scene.engine;
        //create an Image-Observation out of first point
        this.geoframecollection = this.scene.getCurrentGeoFrameCollection();
        this.geoframe = this.scene.getCurrentGeoframe();
        const id = this.geoframe.id;
        const mapinfo = this.scene.getMeasureHandler().mouseToMap(mx, my);
        const map = mapinfo[0].coords;
        const minfo = this.geoframe.getMeasureInfo();
        const senscoor = this.scene.getMeasureHandler().mapToSensor(map);
        let ck;
        if (minfo.streamtype == STREAMTYPE.CALOTTE) {
            ck = minfo.sensors[mapinfo[0].sensorid].focallength;
        }
        else {
            // PROJECTION.PERSPECTIVE -> Stereo Case : images are normaliced to normfocallength in processing
            ck = minfo.imagemeta.focallength;
        }
        let io;
        if (minfo.streamtype == STREAMTYPE.CUBEMAP) {
            io = new ImageObservation(this.geoframe, senscoor.x, senscoor.y, ck, id, mapinfo[0].sensorid, mapinfo[0].cubefaceorientation, mapinfo[0].cubefaceindex);
        }
        else {
            io = new ImageObservation(this.geoframe, senscoor.x, senscoor.y, ck, id, mapinfo[0].sensorid);
        }
        //update an obeservation indicator
        this.scene2d = new Canvas2dutilScene(0, 0, this.scene.engine.getCanvas().width, this.scene.engine.getCanvas().height, this.scene.engine.getCanvas().parentElement);
        this.crosshair = this.scene2d.createCrosshair(mx, my, 30);
        this.scene2d.render();
        const candidateInfos = this.getCandidateGeoframes(mx, my, [8, 12, 18], 2);
        this.measureScene = new ExternalMeasureScene(scene, io, new Vector2(mx, my), candidateInfos, (io) => {
            this.onIOFromExternalScene(io);
        }, (msg) => {
            this.onFailedFormExternalScene(msg);
        });
        //prepare a pointfeature to display the calculated solution
        this.featurecollection = this.scene.getFeatureHandler().createFeatureCollection(new FeatureStyle({
            stylename: "DrawStyle",
            rules: [
                {
                    filter: "",
                    symbolizer: "point",
                    params: {
                        "font-marker": {
                            name: "circle-o",
                        },
                        size: 20,
                        stroke: [1, 0, 0, 0.5],
                    },
                },
            ],
        }), null, "mmMultiPointMeasureSequence_internal", false);
        this.pointfeature = this.featurecollection.createFeature();
        this.pointfeature.geometry = this.pointfeature.createGeometry(GEOMETRYTYPE.POINT);
        this.pointfeature.hide();
        this.multiviewmeasurepoint = this.scene.getMeasureHandler().createMultiViewMeasurePoint(this.scene.getCurrentGeoframe(), (stats) => {
            this._onCalcUpdate(stats);
        }, (stats) => {
            this._onCalcResult(stats);
        });
        this.imageobservations.push(io);
        //check stream types and subscribe events
        if (this.geoframecollection.type == STREAMTYPE.STEREO) {
            this.geoframecollection.rightimagequadtree.onZoom = (d) => {
                this.updateIndicatorPosition();
            };
            this.geoframecollection.rightimagequadtree.onPan = (dx, dy) => {
                this.updateIndicatorPosition();
            };
        }
        else if (this.geoframecollection.type == STREAMTYPE.CALOTTE ||
            this.geoframecollection.type == STREAMTYPE.CUBEMAP) {
            this.panoroteventnr = this.scene.userInputHandler.subscribeEvent(USERINPUTTYPE.PANO_ROTATION, (lat, lon) => {
                this.updateIndicatorPosition();
            }, this);
            this.panozoomeventnr = this.scene.userInputHandler.subscribeEvent(USERINPUTTYPE.PANO_ZOOM, (lat, lon) => {
                this.updateIndicatorPosition();
            }, this);
        }
        else {
            const b = "12";
            //add events for panning or zooming
            this.geoframecollection.imagequadtree.onZoom = (d) => {
                this.updateIndicatorPosition();
            };
            this.geoframecollection.imagequadtree.onPan = (dx, dy) => {
                this.updateIndicatorPosition();
            };
        }
        this.scene.viewer.getComponent("keyboard").deactivate();
    }
    /**
     * callback - a user selected a second point
     * @param io
     */
    onIOFromExternalScene(io) {
        this.imageobservations.push(io);
        this.startWorker();
    }
    /**
     * second point selection failed
     * @param {string} msg
     */
    onFailedFormExternalScene(msg) {
        console.log("acquiring ImageObeservation from external Scene failed.");
    }
    /**
     * algroithm to determine new geoframes for selecting a second image observation
     * returns an array of geoframes and a position per geoframe to rotate camera accordingly
     * @param mx
     * @param my
     * @param approxMeasureDistances
     * @param numOfFramesPerDistance
     * @returns {Array}
     */
    getCandidateGeoframes(mx, my, approxMeasureDistances = [10], numOfFramesPerDistance = 6) {
        const geoframecollection = this.scene.getCurrentGeoFrameCollection();
        const currentGeoframe = this.scene.getCurrentGeoframe();
        const minfo = currentGeoframe.getMeasureInfo();
        //move 8m from projection center into 'click' direction
        const mh = this.scene.getMeasureHandler();
        const mapinfo = mh.mouseToMap(mx, my);
        const map = mapinfo[0].coords;
        const senscoor = mh.mapToSensor(map);
        const img = mh.sensorToImage(senscoor);
        const candidateInfos = [];
        const geoframearraycopy = geoframecollection.geoframearray.slice(0);
        for (let i = 0; i < approxMeasureDistances.length; i++) {
            const mod = mh.imageToModel(img, approxMeasureDistances[i], mapinfo[0].sensorid);
            let p;
            if (minfo.streamtype == STREAMTYPE.CUBEMAP) {
                p = mh.modelToWorld(mod, mapinfo[0].sensorid, null, mapinfo[0].cubefaceorientation);
            }
            else {
                p = mh.modelToWorld(mod, mapinfo[0].sensorid);
            }
            const cmp = (a, b) => {
                const d_a = Math.sqrt(Math.pow(p.x - a.easting, 2) + Math.pow(p.y - a.northing, 2));
                const d_b = Math.sqrt(Math.pow(p.x - b.easting, 2) + Math.pow(p.y - b.northing, 2));
                return d_a - d_b;
            };
            geoframearraycopy.sort(cmp.bind(this));
            for (let j = Math.min(numOfFramesPerDistance, geoframearraycopy.length - 1); j >= 0; j--) {
                if (geoframearraycopy[j] != currentGeoframe) {
                    candidateInfos.push({ geoframe: geoframearraycopy[j].copy(), querypoint: p });
                    geoframearraycopy.splice(j, 1);
                }
            }
        }
        return candidateInfos;
    }
    /**
     * just update position indicator
     * @private
     */
    _onmousewheel() {
        this.scene.infrascene.render();
        this.updateIndicatorPosition();
        window.setTimeout(() => {
            this.scene2d.render();
        }, 10);
    }
    /**
     * Start the Bündelblock-Worker
     */
    startWorker() {
        //set all observations into measurepoint...
        for (let i = 0; i < this.imageobservations.length; i++) {
            if (!this.imageobservations[i].excludeForCalc) {
                this.multiviewmeasurepoint.imageobservations.push(this.imageobservations[i]);
            }
        }
        if (this.approxval) {
            this.multiviewmeasurepoint.calcCoordinates([
                this.approxval.Easting,
                this.approxval.Northing,
                this.approxval.Height,
            ]);
        }
        else {
            this.multiviewmeasurepoint.calcCoordinates();
        }
    }
    /**
     * not used... intended to update progress bar or whatever
     * @param {WorkerStats} stats
     * @private
     */
    _onCalcUpdate(stats) {
        //not used...
    }
    /**
     * point calculation was finished.
     * @param {WorkerStats} stats
     * @private
     */
    _onCalcResult(stats) {
        this.pointfeature["geometry"]["coordinates"] = [
            [
                this.multiviewmeasurepoint.Easting,
                this.multiviewmeasurepoint.Northing,
                this.multiviewmeasurepoint.Height,
            ],
        ];
        this.pointfeature.update(true);
        if (this.scene.multiviewprops.displayEstimationPoint) {
            this.pointfeature.show();
        }
        this._finishmeasurement();
    }
    /**
     * @method destroy aborts the seqeunce
     */
    destroy() {
        this.abortsequence();
        this.measureScene.destroy();
        this.scene2d.destroy();
    }
    /**
     * @method reset resets the sequence to start a new measurement
     */
    reset() {
        this.abortsequence();
        this.failedcbf("jdjd");
    }
    /**
     * check if point determination was successful and call success or failed callbacks
     * @private _finishmeasurement
     */
    _finishmeasurement() {
        this.scene.infrascene.traversalState.lockNavigation = false;
        this.pointfeature.hide();
        if (this.multiviewmeasurepoint.stats.convergence) {
            this.successcbf(this.multiviewmeasurepoint);
        }
        else {
            this.failedcbf("no convergence...");
        }
    }
    /**
     * @public abortsequence aborts the seqeunce, unsubscribes events
     */
    abortsequence() {
        this.keydeleteeventnmbr = null;
        const geoframecollection = this.scene.getCurrentGeoFrameCollection();
        if (geoframecollection.type == STREAMTYPE.STEREO) {
            geoframecollection.rightimagequadtree.onZoom = null;
            geoframecollection.rightimagequadtree.onPan = null;
        }
        else if (geoframecollection.type == STREAMTYPE.CALOTTE || geoframecollection.type == STREAMTYPE.CUBEMAP) {
            this.scene.userInputHandler.unSubscribeEvent(USERINPUTTYPE.PANO_ROTATION, this.panoroteventnr);
            this.scene.userInputHandler.unSubscribeEvent(USERINPUTTYPE.PANO_ZOOM, this.panozoomeventnr);
        }
        else {
            geoframecollection.imagequadtree.onZoom = null;
            geoframecollection.imagequadtree.onPan = null;
        }
        this.scene.viewer.getComponent("keyboard").activate();
    }
    /**
     * updates position indicator on pan or zoom.
     * @private
     */
    updateIndicatorPosition() {
        const mi = this.geoframecollection.measureinfo;
        const mapx = this.imageobservations[0].x / (mi.imagemeta.tilesize * Math.pow(2, mi.imagemeta.maxzoom));
        const mapy = this.imageobservations[0].y / (mi.imagemeta.tilesize * Math.pow(2, mi.imagemeta.maxzoom));
        let mouse;
        if (this.geoframecollection.type == STREAMTYPE.CUBEMAP) {
            mouse = this.scene.getMeasureHandler().mapToMouse(mapx, mapy, this.imageobservations[0].faceindex);
        }
        else {
            mouse = this.scene.getMeasureHandler().mapToMouse(mapx, mapy, this.imageobservations[0].sensorid);
        }
        this.crosshair.position.x = mouse.x;
        this.crosshair.position.y = mouse.y;
        this.scene2d.render();
    }
}
//# sourceMappingURL=panotwoclickmeasuresequence.js.map